import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2, Plus } from "lucide-react";
import { ViewMinisterDialog } from "../components/molecules";
import { MinisterForm, MinistersTable } from "../components/organisms";
import type { Minister } from "@/models/minister";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { Degree } from "@/models/degree";
import type { MinisterFormData, MinisterFormErrors } from "../types";
import type { MinisterLinkedRecords } from "@/data/ministerRepository";

type DeleteDialogState = 'idle' | 'checking' | 'blocked' | 'ready';

interface MinisterListTemplateProps {
  // Data
  ministers: Minister[];
  districts: District[];
  allRegions: Region[];
  degrees: Degree[];

  // State
  isLoading: boolean;
  canEdit: boolean;

  // Scoped column visibility
  hideDistrictColumn?: boolean;
  hideRegionColumn?: boolean;

  // Actions
  onView: (minister: Minister) => void;
  onEdit: (minister: Minister) => void;
  onDelete: (minister: Minister) => void;
  onCreateNew: () => void;

  // View Modal state
  showViewDialog: boolean;
  onCloseViewDialog: () => void;
  viewingMinister: Minister | null;

  // Form Dialog state
  showFormDialog: boolean;
  onCloseFormDialog: () => void;
  formData: MinisterFormData;
  formErrors: MinisterFormErrors;
  formTouched: Partial<Record<keyof MinisterFormData, boolean>>;
  onFormFieldChange: <K extends keyof MinisterFormData>(field: K, value: MinisterFormData[K]) => void;
  onFormFieldBlur: (field: keyof MinisterFormData) => void;
  onFormSubmit: () => void;
  photoPreview: string | null;
  onPhotoSelect: (file: File | null) => void;
  shouldDeletePhoto?: boolean;
  isSubmitting: boolean;
  editingMinister: Minister | null;

  // Delete confirmation
  showDeleteDialog: boolean;
  deleteDialogState: DeleteDialogState;
  linkedRecords: MinisterLinkedRecords | null;
  onCloseDeleteDialog: () => void;
  onConfirmDelete: () => void;
  deletingMinister: Minister | null;
  isDeleting: boolean;
}

/**
 * Template: Layout for minister list page.
 * MinistersTable owns all filtering, sorting, and pagination internally.
 */
export function MinisterListTemplate({
  ministers,
  districts,
  allRegions,
  degrees,
  isLoading,
  canEdit,
  hideDistrictColumn = false,
  hideRegionColumn = false,
  onView,
  onEdit,
  onDelete,
  onCreateNew,
  showViewDialog,
  onCloseViewDialog,
  viewingMinister,
  showFormDialog,
  onCloseFormDialog,
  formData,
  formErrors,
  formTouched,
  onFormFieldChange,
  onFormFieldBlur,
  onFormSubmit,
  photoPreview,
  onPhotoSelect,
  shouldDeletePhoto,
  isSubmitting,
  editingMinister,
  showDeleteDialog,
  deleteDialogState,
  linkedRecords,
  onCloseDeleteDialog,
  onConfirmDelete,
  deletingMinister,
  isDeleting,
}: MinisterListTemplateProps) {
  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ministros</h1>
          <p className="text-muted-foreground">
            Gestiona la información de los ministros
          </p>
        </div>
        {canEdit && (
          <Button onClick={onCreateNew}>
            <Plus className="mr-2 size-4" />
            Nuevo Ministro
          </Button>
        )}
      </div>

      {/* Table (owns search, filters, sort, pagination internally) */}
      <MinistersTable
        ministers={ministers}
        isLoading={isLoading}
        canEdit={canEdit}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        hideDistrictColumn={hideDistrictColumn}
        hideRegionColumn={hideRegionColumn}
      />

      {/* View Dialog */}
      <ViewMinisterDialog
        minister={viewingMinister}
        open={showViewDialog}
        onOpenChange={onCloseViewDialog}
        districts={districts}
        regions={allRegions}
        degrees={degrees}
      />

      {/* Form Dialog (Create/Edit) */}
      <Dialog open={showFormDialog} onOpenChange={onCloseFormDialog}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMinister ? "Editar Ministro" : "Nuevo Ministro"}
            </DialogTitle>
            <DialogDescription>
              {editingMinister
                ? "Actualiza la información del ministro"
                : "Completa el formulario para agregar un nuevo ministro"}
            </DialogDescription>
          </DialogHeader>
          <MinisterForm
            formData={formData}
            errors={formErrors}
            touched={formTouched}
            onFieldChange={onFormFieldChange}
            onFieldBlur={onFormFieldBlur}
            onSubmit={onFormSubmit}
            onCancel={onCloseFormDialog}
            districts={districts}
            regions={allRegions}
            degrees={degrees}
            photoPreview={photoPreview}
            onPhotoSelect={onPhotoSelect}
            shouldDeletePhoto={shouldDeletePhoto}
            isSubmitting={isSubmitting}
            isReadOnly={false}
            existingMinister={editingMinister || undefined}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Dialog — 3 states: checking / blocked / ready */}
      <AlertDialog open={showDeleteDialog} onOpenChange={onCloseDeleteDialog}>
        <AlertDialogContent>
          {deleteDialogState === 'checking' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Verificando registros...</AlertDialogTitle>
                <AlertDialogDescription>
                  Comprobando si <strong>{deletingMinister?.full_name}</strong> tiene registros vinculados.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex justify-center py-4">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
              </div>
            </>
          )}

          {deleteDialogState === 'blocked' && linkedRecords && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="size-5 text-destructive" />
                  No se puede eliminar
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <strong>{deletingMinister?.full_name}</strong> tiene registros vinculados y no puede ser eliminado permanentemente.
                  Usa &quot;Dar de baja&quot; en su lugar.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <ul className="my-2 space-y-1 rounded-md border bg-muted/40 px-4 py-3 text-sm">
                {linkedRecords.credentialRequests > 0 && (
                  <li>Solicitudes de credencial: <strong>{linkedRecords.credentialRequests}</strong></li>
                )}
                {linkedRecords.authorityPositions > 0 && (
                  <li>Cargos de autoridad: <strong>{linkedRecords.authorityPositions}</strong></li>
                )}
                {linkedRecords.eventParticipations > 0 && (
                  <li>Participaciones en eventos: <strong>{linkedRecords.eventParticipations}</strong></li>
                )}
                {linkedRecords.eventGuests > 0 && (
                  <li>Invitado en eventos: <strong>{linkedRecords.eventGuests}</strong></li>
                )}
                {linkedRecords.institutionAuthorities > 0 && (
                  <li>Autoridades institucionales: <strong>{linkedRecords.institutionAuthorities}</strong></li>
                )}
                {linkedRecords.institutionMemberships > 0 && (
                  <li>Membresías institucionales: <strong>{linkedRecords.institutionMemberships}</strong></li>
                )}
              </ul>
              <AlertDialogFooter>
                <AlertDialogCancel>Cerrar</AlertDialogCancel>
              </AlertDialogFooter>
            </>
          )}

          {deleteDialogState === 'ready' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar ministro permanentemente?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción eliminará a <strong>{deletingMinister?.full_name}</strong> de forma permanente e irreversible.
                  Solo procede si fue ingresado por error.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onConfirmDelete}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? "Eliminando..." : "Eliminar permanentemente"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
